import React, { useState, useEffect } from 'react';

// Material UI
import {
    styled,
    Box,
    Typography,
    DialogContentText,
    CircularProgress,
    FormGroup,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { FormControl, FormControlLabel, Select, RadioGroup, Radio, Checkbox } from '@mui/material';
import { ToggleButton as _ToggleButton, ToggleButtonGroup } from '@mui/material';
import { StyledEngineProvider, alpha } from '@mui/material/styles';

// Components
import { LoadingState, ErrorState } from 'components/ui/states';
import { ActionButton } from 'components/ui/buttons';
import { TextField, TextArea, DatePicker } from 'components/ui/inputs';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import { useTextInput, useNumberInput, useDatePicker, useToggle } from 'hooks/forms';
import { useNotification } from 'context/notification';
import { FieldTypes } from 'utility/registration';
import moment from 'moment-timezone';

export default function RegistrationAnswers(props) {
    const { eventId, tranId, refreshToken } = props;

    // refreshToken = parent triggered refresh
    // state.refresh = locally triggered refresh

    const [state, setState] = useState({
        attendees: null,
        showFirstName: false,
        showLastName: false,
        showEmail: false,
        loading: true,
        error: false,
        refresh: 0,
    });

    // Set these fields to trigger the edit dialog to open

    const [editFirstName, setEditFirstName] = useState({
        open: false,
        attendeeId: null,
        value: null,
    });
    const [editLastName, setEditLastName] = useState({
        open: false,
        attendeeId: null,
        value: null,
    });
    const [editEmail, setEditEmail] = useState({
        open: false,
        attendeeId: null,
        value: null,
    });

    const [editState, setEditState] = useState({
        open: false,
        attendeeId: null,
        questionId: null,
    });

    useEffect(() => {
        fetchExpresso(`/apiv2/events/${eventId}/orders/${tranId}/attendees`)
            .then(async (res) => {
                if (res.status === 404) {
                    // There are no checkout responses to display
                    setState((s) => ({
                        ...s,
                        attendees: [],
                        showFirstName: false,
                        showLastName: false,
                        showEmail: false,
                        loading: false,
                        error: false,
                    }));
                    return;
                }

                if (res.status !== 200) {
                    // Something went wrong
                    throw new Error();
                }

                const { attendees, showFirstName, showLastName, showEmail } = await res.json();

                setState((s) => ({
                    ...s,
                    attendees,
                    showFirstName,
                    showLastName,
                    showEmail,
                    loading: false,
                    error: false,
                }));
            })
            .catch((e) => {
                setState((s) => ({ ...s, loading: false, error: true }));
            });
    }, [tranId, eventId, state.refresh, refreshToken]);

    const openEditDialog = (attendeeId, questionId) => {
        setEditState((s) => ({ ...s, open: true, attendeeId, questionId }));
    };

    const closeEditDialog = () => {
        setEditState((s) => ({ ...s, open: false, attendeeId: null, question: null }));
    };

    const handleEditResponse = () => {
        setState((s) => ({ ...s, refresh: s.refresh + 1 }));
        closeEditDialog();
    };

    if (state.loading) {
        return (
            <Card>
                <CardHeader title="Checkout Form Responses" />
                <CardContent>
                    <LoadingState />
                </CardContent>
            </Card>
        );
    }

    if (state.error) {
        return (
            <Card>
                <CardHeader title="Checkout Form Responses" />
                <CardContent>
                    <ErrorState message="Unable to load checkout answers" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader title="Checkout Form Responses" />
            <CardContent>
                {state.attendees.length === 0 && (
                    <Typography>There are no checkout responses for this order</Typography>
                )}

                <AttendeeResponses
                    attendees={state.attendees}
                    showFirstName={state.showFirstName}
                    showLastName={state.showLastName}
                    showEmail={state.showEmail}
                    onEditFirstName={(attendee) =>
                        setEditFirstName({
                            open: true,
                            attendeeId: attendee.id,
                            value: attendee.firstName,
                        })
                    }
                    onEditLastName={(attendee) =>
                        setEditLastName({
                            open: true,
                            attendeeId: attendee.id,
                            value: attendee.lastName,
                        })
                    }
                    onEditEmail={(attendee) =>
                        setEditEmail({
                            open: true,
                            attendeeId: attendee.id,
                            value: attendee.email,
                        })
                    }
                    onEditResponse={(attendeeId, questionId) => openEditDialog(attendeeId, questionId)}
                />
            </CardContent>

            {editFirstName.open && (
                <FirstNameDialog
                    eventId={eventId}
                    orderId={tranId}
                    attendeeId={editFirstName.attendeeId}
                    value={editFirstName.value}
                    onClose={() => setEditFirstName({ open: false, attendeeId: null, value: null })}
                    onEdit={() => {
                        setEditFirstName({ open: false, attendeeId: null, value: null });
                        setState((s) => ({ ...s, refresh: s.refresh + 1 }));
                    }}
                />
            )}

            {editLastName.open && (
                <LastNameDialog
                    eventId={eventId}
                    orderId={tranId}
                    attendeeId={editLastName.attendeeId}
                    value={editLastName.value}
                    onClose={() => setEditLastName({ open: false, attendeeId: null, value: null })}
                    onEdit={() => {
                        setEditLastName({ open: false, attendeeId: null, value: null });
                        setState((s) => ({ ...s, refresh: s.refresh + 1 }));
                    }}
                />
            )}

            {editEmail.open && (
                <EmailDialog
                    eventId={eventId}
                    orderId={tranId}
                    attendeeId={editEmail.attendeeId}
                    value={editEmail.value}
                    onClose={() => setEditEmail({ open: false, attendeeId: null, value: null })}
                    onEdit={() => {
                        setEditEmail({ open: false, attendeeId: null, value: null });
                        setState((s) => ({ ...s, refresh: s.refresh + 1 }));
                    }}
                />
            )}

            {editState.open && (
                <EditDialog
                    eventId={eventId}
                    orderId={tranId}
                    attendeeId={editState.attendeeId}
                    questionId={editState.questionId}
                    onClose={closeEditDialog}
                    onEdit={handleEditResponse}
                />
            )}
        </Card>
    );
}

function Answer(props) {
    const { type, answer } = props;

    if (answer === null) {
        return <Typography>-</Typography>;
    }

    if (type === 8) {
        // Yes or No answer. Convert 1/0 to text
        return answer === '1' ? <Typography>Yes</Typography> : <Typography>No</Typography>;
    }

    if (type === 9) {
        return <Typography>{moment(answer).format('YYYY-MM')}</Typography>;
    }

    if (type === 10) {
        // Terms and Conditions answer. Convert 1/0 to text
        return answer === '1' ? (
            <Typography>Accepted</Typography>
        ) : (
            <Typography>Rejected</Typography>
        );
    } else {
        return <Typography>{answer}</Typography>;
    }
}

function AttendeeResponses(props) {
    const {
        attendees,
        showFirstName,
        showLastName,
        showEmail,
        onEditFirstName,
        onEditLastName,
        onEditEmail,
        onEditResponse,
    } = props;

    return attendees.map((attendee, attendeeIndex) => {
        const isReadOnly = attendee.isVoid === 1;
        const isAttendee = Boolean(attendee.name);

        // Attendee Name (if applicable)
        let titleContents = [
            attendee.isVoid ? '[VOID]' : '',
            attendee.unipassNumber ? `#${String(attendee.unipassNumber).padStart(3, '0')}:` : '',
            attendee.name ? attendee.name : ''
        ];
        const title = titleContents.join(' ');

        return (
            <Box key={attendeeIndex} mb={4}>
                <Table size='small'>
                    {isAttendee && (
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <AttendeeName sx={isReadOnly ? { color: '#777' } : undefined}>{title}</AttendeeName>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    )}
                    <TableBody>
                        {showFirstName && (
                            <TableRow>
                                <TableCell width={120}>First Name</TableCell>
                                <TableCell>
                                    <Answer answer={attendee.firstName} />
                                </TableCell>
                                <TableCell width={30}>
                                    <ActionButton
                                        size="small"
                                        disabled={isReadOnly}
                                        onClick={() => onEditFirstName(attendee)}
                                    >
                                        Edit
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        )}
                        {showLastName && (
                            <TableRow>
                                <TableCell width={120}>Last Name</TableCell>
                                <TableCell>
                                    <Answer answer={attendee.lastName} />
                                </TableCell>
                                <TableCell width={30}>
                                    <ActionButton
                                        size="small"
                                        disabled={isReadOnly}
                                        onClick={() => onEditLastName(attendee)}
                                    >
                                        Edit
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        )}
                        {showEmail && (
                            <TableRow>
                                <TableCell width={120}>Email</TableCell>
                                <TableCell>
                                    <Answer answer={attendee.email} />
                                </TableCell>
                                <TableCell width={30}>
                                    <ActionButton
                                        size="small"
                                        disabled={isReadOnly}
                                        onClick={() => onEditEmail(attendee)}
                                    >
                                        Edit
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        )}
                        {attendee.responses.map((response, responseIndex) => (
                            <TableRow key={attendeeIndex + '-' + responseIndex}>
                                <TableCell width={120}>{response.label}</TableCell>
                                <TableCell>
                                    <Answer type={response.type} answer={response.answer} />
                                </TableCell>
                                <TableCell width={30}>
                                    <ActionButton
                                        size="small"
                                        disabled={isReadOnly}
                                        onClick={() => onEditResponse(attendee.id, response.questionId)}
                                    >
                                        Edit
                                    </ActionButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );
    });
}

function FirstNameDialog(props) {
    const { onEdit, onClose, eventId, orderId, attendeeId, value } = props;

    const [loading, setLoading] = useState(false);

    const firstName = useTextInput({ defaultValue: value, maxLength: 75 });

    const { notify } = useNotification();

    const onSubmit = () => {
        if (!firstName.value.trim()) {
            notify.error('This field cannot be empty');
            return;
        }

        setLoading(true);

        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { firstName: firstName.value.trim() },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem updating this response');
                setLoading(false);
            });
    };

    const onDelete = () => {
        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { firstName: null },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem deleting this response');
                setLoading(false);
            });
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>Edit Answer</DialogTitle>
            <DialogContent>
                <DialogContentText>First Name</DialogContentText>
                <TextField {...firstName.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete}>Delete</DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton
                    onClick={onSubmit}
                    disabled={loading || firstName.value.trim().length === 0}
                >
                    Save
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function LastNameDialog(props) {
    const { onEdit, onClose, eventId, orderId, attendeeId, value } = props;

    const [loading, setLoading] = useState(false);

    const lastName = useTextInput({ defaultValue: value, maxLength: 75 });

    const { notify } = useNotification();

    const onSubmit = () => {
        if (!lastName.value.trim()) {
            notify.error('This field cannot be empty');
            return;
        }

        setLoading(true);

        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { lastName: lastName.value.trim() },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem updating this response');
                setLoading(false);
            });
    };

    const onDelete = () => {
        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { lastName: null },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem deleting this response');
                setLoading(false);
            });
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>Edit Answer</DialogTitle>
            <DialogContent>
                <DialogContentText>Last Name</DialogContentText>
                <TextField {...lastName.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete}>Delete</DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton
                    onClick={onSubmit}
                    disabled={loading || lastName.value.trim().length === 0}
                >
                    Save
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function EmailDialog(props) {
    const { onEdit, onClose, eventId, orderId, attendeeId, value } = props;

    const [loading, setLoading] = useState(false);

    const email = useTextInput({ defaultValue: value, maxLength: 75 });

    const { notify } = useNotification();

    const onSubmit = () => {
        if (!email.value.trim()) {
            notify.error('This field cannot be empty');
            return;
        }

        setLoading(true);

        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { email: email.value.trim() },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem updating this response');
                setLoading(false);
            });
    };

    const onDelete = () => {
        fetchExpresso(`/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}`, {
            method: 'PUT',
            body: { email: null },
        })
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem deleting this response');
                setLoading(false);
            });
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>Edit Answer</DialogTitle>
            <DialogContent>
                <DialogContentText>Email Address</DialogContentText>
                <TextField {...email.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete}>Delete</DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton
                    onClick={onSubmit}
                    disabled={loading || email.value.trim().length === 0}
                >
                    Save
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function EditDialog(props) {
    const { onEdit, onClose, eventId, orderId, attendeeId, questionId } = props;

    const [state, setState] = useState({
        question: null,
        status: 'loading', // loading, error, success
        submitting: false,
    });

    const { notify } = useNotification();

    useEffect(() => {
        fetchExpresso(
            `/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}/questions/${questionId}`,
        )
            .then((res) => res.json())
            .then((data) => {
                setState((s) => ({ ...s, question: data.question, status: 'success' }));
            })
            .catch(() => {
                setState((s) => ({ ...s, error: true, status: 'error' }));
                notify.error('There was a problem loading this question');
            });
        // eslint-disable-next-line
    }, [eventId, orderId, attendeeId, questionId]);

    const onSubmit = (answer) => {
        setState((s) => ({ ...s, submitting: true }));

        fetchExpresso(
            `/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}/questions/${questionId}`,
            {
                method: 'PUT',
                body: { answer },
            },
        )
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem updating this response');
                setState((s) => ({ ...s, submitting: false }));
            });
    };

    const onDelete = () => {
        fetchExpresso(
            `/apiv2/events/${eventId}/orders/${orderId}/attendees/${attendeeId}/questions/${questionId}`,
            {
                method: 'DELETE',
            },
        )
            .then((res) => {
                if (res.status !== 200) {
                    throw new Error();
                }
                onEdit();
            })
            .catch((e) => {
                notify.error('There was a problem deleting this response');
                setState((s) => ({ ...s, submitting: false }));
            });
    };

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <DialogTitle>Edit Answer</DialogTitle>
            {state.status === 'loading' && (
                <DialogContent>
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        height={120}
                        mb={2}
                    >
                        <CircularProgress />
                    </Box>
                </DialogContent>
            )}
            {state.status === 'error' && (
                <DialogContent>
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        height={120}
                        mb={2}
                    >
                        <DialogContentText>
                            There was a problem loading this question
                        </DialogContentText>
                    </Box>
                </DialogContent>
            )}
            {state.status === 'success' && (
                <>
                    {state.question.type === FieldTypes.SHORT_ANSWER && (
                        <ShortAnswer
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.text}
                        />
                    )}
                    {state.question.type === FieldTypes.PARAGRAPH && (
                        <Paragraph
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.text}
                        />
                    )}
                    {state.question.type === FieldTypes.SINGLE_CHOICE && (
                        <SingleChoice
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.options}
                            options={state.question.options}
                        />
                    )}
                    {state.question.type === FieldTypes.MULTIPLE_CHOICE && (
                        <MultipleChoice
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.options}
                            options={state.question.options}
                        />
                    )}
                    {state.question.type === FieldTypes.DROPDOWN && (
                        <Dropdown
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.options}
                            options={state.question.options}
                        />
                    )}
                    {state.question.type === FieldTypes.FULL_DATE && (
                        <FullDate
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.date}
                        />
                    )}
                    {state.question.type === FieldTypes.AGE && (
                        <Age
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.integer}
                        />
                    )}
                    {state.question.type === FieldTypes.YES_OR_NO && (
                        <YesOrNo
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.boolean}
                        />
                    )}
                    {state.question.type === FieldTypes.SHORT_DATE && (
                        <ShortDate
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.date}
                        />
                    )}
                    {state.question.type === FieldTypes.TERMS_AND_CONDITIONS && (
                        <TermsAndConditions
                            onSubmit={onSubmit}
                            onClose={onClose}
                            onDelete={onDelete}
                            loading={state.submitting}
                            question={state.question.description}
                            value={state.question.answer.boolean}
                        />
                    )}
                </>
            )}
        </Dialog>
    );
}

function ShortAnswer({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useTextInput({ defaultValue: value, maxLength: 2000 });

    const allowSubmit =
        loading === false && userInput.value !== value && userInput.value.trim() !== '';
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: userInput.value.trim(),
            date: null,
            integer: null,
            boolean: null,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <TextField {...userInput.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function Paragraph({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useTextInput({ defaultValue: value, maxLength: 2000 });

    const allowSubmit =
        loading === false && userInput.value !== value && userInput.value.trim() !== '';
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: userInput.value.trim(),
            date: null,
            integer: null,
            boolean: null,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <TextArea {...userInput.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function SingleChoice({ question, value, options, onClose, onSubmit, onDelete, loading }) {
    // Option IDs are numbers, but we store them as strings in the form since RadioButtons expect strings
    // There will only be one ID in the answer array
    const userInput = useTextInput({ defaultValue: String(value[0] || '') });

    const allowSubmit = loading === false && userInput.value !== value && userInput.value !== '';
    const allowDelete = loading === false && value.length > 0;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: null,
            boolean: null,
            options: [Number(userInput.value)],
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <FormControl>
                    <RadioGroup
                        aria-label={'single choice'}
                        name={'single choice'}
                        {...userInput.formProps}
                    >
                        {options.map((o) => (
                            <FormControlLabel
                                key={o.id}
                                value={String(o.id)}
                                control={<Radio />}
                                label={o.label}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function MultipleChoice({ question, value, options, onClose, onSubmit, onDelete, loading }) {
    const [selected, setSelected] = useState(new Set(value));

    const allowSubmit = loading === false && selected.size > 0;
    const allowDelete = loading === false && value.length > 0;

    const handleChange = (id, checked) => {
        const newSelected = new Set(selected);
        checked ? newSelected.add(id) : newSelected.delete(id);
        setSelected(newSelected);
    };

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: null,
            boolean: null,
            options: Array.from(selected),
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <FormControl>
                    <FormGroup>
                        {options.map((o) => (
                            <FormControlLabel
                                key={o.id}
                                control={
                                    <Checkbox
                                        checked={selected.has(o.id)}
                                        onChange={(e) => handleChange(o.id, e.target.checked)}
                                    />
                                }
                                label={o.label}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function Dropdown({ question, value, options, onClose, onSubmit, onDelete, loading }) {
    // Option IDs are numbers, but we store them as strings in the form since the dropdown picker uses string values
    // There will only be one ID in the answer array
    const userInput = useTextInput({ defaultValue: String(value[0] || '') });

    const allowSubmit = loading === false && userInput.value !== value && userInput.value !== '';
    const allowDelete = loading === false && value.length > 0;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: null,
            boolean: null,
            options: [Number(userInput.value)],
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <FormControl variant="outlined">
                    <Select native {...userInput.formProps}>
                        <option disabled value=""></option>
                        {options.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.label}
                            </option>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function FullDate({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useDatePicker({ defaultValue: value });

    const allowSubmit = loading === false && userInput.value !== null && userInput.value.isValid();
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: userInput.value.format('YYYY-MM-DD'),
            integer: null,
            boolean: null,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <StyledEngineProvider injectFirst>
                    <DatePicker {...userInput.formProps} inputFormat="YYYY-MM-DD" />
                </StyledEngineProvider>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function Age({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useNumberInput({ defaultValue: value, integerOnly: true, maxLength: 3 });

    const allowSubmit =
        loading === false && Number(userInput.value) !== value && userInput.value.trim() !== '';
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: userInput.value,
            boolean: null,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <TextField {...userInput.formProps} />
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function YesOrNo({ question, value, onClose, onSubmit, onDelete, loading }) {
    const [boolean, setBoolean] = useState(value ?? '');

    const allowSubmit = loading === false && boolean !== String(value) && boolean !== '';
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: null,
            boolean: boolean,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <FormControl>
                    <ToggleButtonGroup
                        value={String(boolean)}
                        size="small"
                        exclusive
                        onChange={(e, v) => setBoolean(v === 'true')}
                        aria-label={'yes or no'}
                    >
                        <ToggleButton value="true" aria-label={'yes'}>
                            <Typography>Yes</Typography>
                        </ToggleButton>
                        <ToggleButton value="false" aria-label={'no'}>
                            <Typography>No</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function ShortDate({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useDatePicker({ defaultValue: value });

    const allowSubmit = loading === false && userInput.value !== null && userInput.value.isValid();
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: userInput.value.format('YYYY-MM-01'),
            integer: null,
            boolean: null,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <StyledEngineProvider injectFirst>
                    <DatePicker
                        {...userInput.formProps}
                        inputFormat="YYYY-MM"
                        views={['year', 'month']}
                    />
                </StyledEngineProvider>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function TermsAndConditions({ question, value, onClose, onSubmit, onDelete, loading }) {
    const userInput = useToggle({ defaultValue: value }); // Defaults to false if user did not respond to the question

    const allowSubmit = loading === false && userInput.checked !== value;
    const allowDelete = loading === false && value !== null;

    const handleSubmit = () => {
        onSubmit({
            text: null,
            date: null,
            integer: null,
            boolean: userInput.checked,
            options: null,
        });
    };

    return (
        <>
            <DialogContent>
                <DialogContentText>{question}</DialogContentText>
                <FormControl>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox {...userInput.formProps} />}
                            label={'I agree'}
                        />
                    </FormGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={!allowDelete}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose}>Cancel</CancelButton>
                <SubmitButton onClick={handleSubmit} disabled={!allowSubmit}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

const AttendeeName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
}));

const ToggleButton = styled(_ToggleButton)(({ theme }) => ({
    width: 100,
    borderColor: theme.palette.grey[700],
    color: theme.palette.grey[700],
    '&.Mui-selected': {
        borderColor: theme.palette.info.dark,
        color: theme.palette.info.dark,
        backgroundColor: alpha(theme.palette.info.light, 0.2),
    },
}));
